import { RGBColor } from '@shared/helpers/color.helpers';

export const canvasColors = {
  lineSelected: '#1976d2',
  line: '#cccccc',
  lineGray: '#cccccc',
  lineBlue: '#1976d2',
  /**
   * Softer guide for storeys below the selected floor in 2D — keeps PDF black
   * outlines distinguishable from Nodon geometry. Unused in 3D.
   */
  lineGuideBelow: '#9e9e9e',
  /** White line drawn behind blue selected outline (halo). */
  lineSelectedHalo: '#ffffff',
  pointSelected: '#1976d2',
  pointFill: '#fff',
  shapeFill: '#ecf1f9',
  shapeFillSelected: '#E3ECF8',
  mezzanineFill: '#ffcfdf',
  draftShapeFill: 'transparent',
  centerLineColor: 'rgba(0, 0, 0, 0.1)',
};

/** Shared building selection silhouette sizes in CSS pixels. */
export const BUILDING_SELECTION_OUTLINE_WIDTH_PX = 2;
export const BUILDING_SELECTION_HALO_WIDTH_PX = 4;

export const DELETE_POINT_TRESHOLD = 5;
export const POINT_DRAG_CLICK_WINDOW_MS = 300;
export const MAPBOX_DEFAULT_CLICK_TOLERANCE_PX = 3;
/** If pointer moves more than this (px) between down and up, treat as drag not click. */
export const CLICK_DRAG_DISTANCE_THRESHOLD_PX = 10;

export const LINE_HIT_PADDING_PX = 6;
export const POINT_HIT_PADDING_PX = 8;
export const POINT_SNAP_DISTANCE_PX = 5;

/** Opacity for Mapbox + sibling buildings during click-anywhere draw mode. */
export const CLICK_ANYWHERE_OTHER_BUILDINGS_OPACITY = 0.3;

export const LOCATION_PIN_METERS_PER_PIXEL = 1;
export const LOCATION_PIN_SIZE_PX = 15;
export const LOCATION_PIN_DEFAULT_OPACITY = 0.8;
export const LOCATION_PIN_ACTIVE_OPACITY = 1;
export const LOCATION_PIN_ICON_ID = 'map-location-pin-icon';
export const LOCATION_PIN_ICON_NATIVE_SIZE = 256;

/**
 * Light gray plate behind the building / PDF underlay when the Mapbox basemap
 * is hidden — not pure white so a white sheet still reads as a page.
 */
export const DESIGN_CANVAS_PLATE_COLOR = '#e8e8e8';

/**
 * Mapbox source/layer ids: full-world white fill in slot `top` that covers the Mapbox basemap
 * (streets/satellite) when the user turns the background map off.
 */
export const MAPBOX_BASEMAP_MASK_SOURCE_ID = 'nodon-mapbox-basemap-mask-source';
export const MAPBOX_BASEMAP_MASK_LAYER_ID = 'nodon-mapbox-basemap-mask-layer';

/** Mapbox image source + raster layer for the plan underlay. */
export const MAPBOX_BLUEPRINT_SOURCE_ID = 'nodon-blueprint-underlay-source';
export const MAPBOX_BLUEPRINT_LAYER_ID = 'nodon-blueprint-underlay-layer';
/** Custom WebGL plane for underlays at the selected storey elevation. */
export const MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID =
  'nodon-blueprint-underlay-elevated';

/** Outline / handles while the underlay is unlocked for transform. */
export const MAPBOX_BLUEPRINT_OUTLINE_SOURCE_ID =
  'nodon-blueprint-underlay-outline-source';
export const MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID =
  'nodon-blueprint-underlay-outline-layer';
export const MAPBOX_BLUEPRINT_HANDLES_LAYER_ID =
  'nodon-blueprint-underlay-handles-layer';

/** Calibration measure line. */
export const MAPBOX_BLUEPRINT_CALIBRATE_SOURCE_ID =
  'nodon-blueprint-underlay-calibrate-source';
export const MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID =
  'nodon-blueprint-underlay-calibrate-line';

export const mapRenderingConstants = {
  // Map rendering sizes (CSS px; scaled by DPR at render time)
  dashSizePx: 3,
  gapSizePx: 3,
  lineWidthPx: 1.5,
  pointSizePx: 10,
  /** Point size when hovered or selected. */
  pointSizeHoverPx: 12,
  /** Selected point size while hovered; keeps the same +2px hover growth. */
  pointSizeSelectedHoverPx: 14,
  /** Pen-tool draft vertex diameter (DEV-2576). */
  penPointSizePx: 15,
  /** Screen-pixel radius for closing an Alt-draw draft on the first vertex. */
  altDrawCloseDistancePx: 10,
  /** Stroke scale for selected point (white ring); 0.5 = 50% thinner than default. */
  pointStrokeScaleSelected: 0.5,

  /** Line width scale factors (1 = base). */
  lineWidthScaleVertical: 0.67,
  lineWidthScaleHorizontal: 2,
  /** Blue outline and white halo for the selected storey, in CSS pixels. */
  lineWidthSelectedPx: 4,
  lineWidthSelectedHaloPx: 9.5,

  /**
   * Line thickness zoom scaling: below this zoom level, line width scales down.
   * At zoom >= this value, scale = 1 (lines look as today).
   */
  lineWidthZoomThreshold: 18,
  /**
   * How fast line width decreases when zoomed out below threshold.
   * scale = 2^((zoom - lineWidthZoomThreshold) * lineWidthZoomFactor).
   * Higher = thinner lines faster when zooming out.
   */
  lineWidthZoomFactor: 0.4,
  /** Minimum scale so lines don't vanish at far zoom. */
  lineWidthZoomMinScale: 0.15,

  // Extrusion rendering constants
  floorOpacity: 0.5,
  groundFloorOpacity: 0.85,
  wallOpacity: 0.2,
  roofOpacity: 0.2,
  undergroundWallOpacity: 0.8,
  undergroundWallColor: [0.9, 0.9, 0.9] satisfies RGBColor,
  floorOffsetMeters: 0.02,
  undergroundFloorOpacity: 0.2,
};

// Henrik: keep line thickness in CSS pixels; WebGL uses device pixels so we scale by DPR at render time.

// Point diameters in CSS pixels (mapbox canvas is device px; shaders scale by DPR).

/**
 * Line width scale factor from current zoom. Returns 1 when zoom >= threshold; below threshold
 * scale decreases (lines get thinner). Configurable via lineWidthZoomThreshold, lineWidthZoomFactor, lineWidthZoomMinScale.
 */
export function getLineWidthZoomScale(zoom: number): number {
  const { lineWidthZoomThreshold, lineWidthZoomFactor, lineWidthZoomMinScale } =
    mapRenderingConstants;
  if (zoom >= lineWidthZoomThreshold) return 1;
  const scale = Math.pow(
    2,
    (zoom - lineWidthZoomThreshold) * lineWidthZoomFactor,
  );
  return Math.max(scale, lineWidthZoomMinScale);
}
